import {
  Component,
  OnDestroy,
  Type,
  computed,
  inject,
  OnInit,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { NgClass } from '@angular/common';
import { PrimaryButtonComponent } from '../../../../../shared/components/primary-button/primary-button.component';
import { StepIndicatorComponent } from '../../../../../shared/components/step-indicator/step-indicator.component';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { LearningPlanSetupConfigurationComponent } from './components/learning-plan-setup-configuration/learning-plan-setup-configuration.component';
import { LearningPlanSetupEnrollmentComponent } from './components/learning-plan-setup-enrollment/learning-plan-setup-enrollment.component';
import { MatIcon } from '@angular/material/icon';
import { TextButtonComponent } from '../../../../../shared/components/text-button/text-button.component';
import { Router } from '@angular/router';
import { LearningPlanBuilderDataService } from '../../services/learning-plan-builder-data.service';
import { MatTooltip } from '@angular/material/tooltip';
import { CourseDetailCardComponent } from '../../../course/components/course-detail-card/course-detail-card.component';
import { toCourseGalleryItemDTO } from '../../../course/mappers/dto-mappers';
import { CourseGalleryService } from '../../services/course-gallery.service';
import { DIALOG_SIZE_LARGE } from '../../../../../shared/components/dialog/dialog-sizes';
import { LearningPlanDataService } from '../../services/learning-plan-data.service';
import { PrecuratedLearningPlanDetailsDto } from '../../models/PrecuratedLearningPlanDTO';
import { CourseIconMappingService } from '../../../../../shared/services/course-icon-mapping.service';
import { LoadingComponent } from '../../../../../shared/components/loading/loading.component';
import { AnalyticsService } from '../../../../../core/services/analytics/analytics.service';

interface Step {
  key: string;
  component: Type<unknown>;
  completed: boolean;
  control?: FormGroup;
}

@Component({
  selector: 'app-learning-plan-setup',
  imports: [
    TranslocoModule,
    PrimaryButtonComponent,
    StepIndicatorComponent,
    MatIcon,
    NgClass,
    TextButtonComponent,
    MatTooltip,
    LearningPlanSetupConfigurationComponent,
    LearningPlanSetupEnrollmentComponent,
    LoadingComponent,
  ],
  templateUrl: './learning-plan-setup.component.html',
  styleUrl: './learning-plan-setup.component.scss',
})
export class LearningPlanSetupComponent implements OnDestroy, OnInit {
  protected dialog = inject(MatDialog);
  private readonly injectedData = inject<{
    isCustomTraining: boolean;
  }>(MAT_DIALOG_DATA);
  protected data = { isCustomTraining: this.injectedData.isCustomTraining };
  protected readonly router = inject(Router);
  protected readonly dataService = inject(LearningPlanBuilderDataService);
  private readonly dialogRef =
    inject<MatDialogRef<LearningPlanSetupComponent>>(MatDialogRef);
  private readonly courseGalleryService = inject(CourseGalleryService);
  protected readonly learningPlanService = inject(LearningPlanDataService);
  private readonly courseIconMappingService = inject(CourseIconMappingService);
  private readonly transloco = inject(TranslocoService);
  private readonly analyticsService = inject(AnalyticsService);

  readonly precuratedLearningPlan =
    computed<PrecuratedLearningPlanDetailsDto | null>(() => {
      const plans = this.learningPlanService.precuratedLearningPlans();
      return plans[0] ?? null;
    });
  readonly isPrecuratedLearningPlansLoading =
    this.learningPlanService.isLoadingPrecuratedLearningPlans;

  selectedIndex = 0;
  showOtherOptions = false;
  isPreCuratedTraining = false;
  canEnroll = false;

  get isAdvancedOptionsLocked(): boolean {
    return (
      this.dataService.formGroupSettings.controls.deadlineType.value === 'fixed'
    );
  }

  get hasPrecuratedLearningPlan(): boolean {
    const courseId = this.dataService.getOriginallySelectedCourse()?.id;
    return !!this.precuratedLearningPlan()?.phases.some((phase) =>
      phase.courses.some((course) => course.id === courseId),
    );
  }

  readonly currentPhase = computed(() =>
    this.phases().find(
      (p) => p.course.id === this.dataService.getOriginallySelectedCourse()?.id,
    ),
  );

  private readonly destroy$: Subject<void> = new Subject();

  readonly steps: readonly Step[] = [
    {
      key: 'configuration',
      component: LearningPlanSetupConfigurationComponent,
      completed: false,
      control: this.dataService.formGroupSettings,
    },
    {
      key: 'enrollment',
      component: LearningPlanSetupEnrollmentComponent,
      completed: false,
    },
  ];

  readonly phases = computed(() => {
    const phaseOrder = ['YearOne', 'YearTwo', 'YearThree'];

    return (this.precuratedLearningPlan()?.phases ?? [])
      .map((phase) => ({
        ...phase,
        course: phase.courses[0],
      }))
      .sort((a, b) => phaseOrder.indexOf(a.name) - phaseOrder.indexOf(b.name));
  });

  constructor() {
    void this.dataService.prefetchUsers();
  }

  ngOnInit(): void {
    this.loadLearningPlans();
  }

  private loadLearningPlans(): void {
    const topicAreaId =
      this.dataService.getOriginallySelectedCourse()?.topicAreaId;
    if (!topicAreaId) {
      return;
    }
    this.learningPlanService.loadPrecuratedLearningPlans(topicAreaId);
  }

  public getCourseIcon(courseName: string): string {
    const topicAreaName = this.precuratedLearningPlan()?.topicArea.name;
    return this.courseIconMappingService.getCourseIcon(
      courseName,
      topicAreaName,
    );
  }

  getPhaseNumber(name?: string): string {
    if (!name) {
      return '';
    }
    let num: number;
    switch (name) {
      case 'YearOne':
        num = 1;
        break;
      case 'YearTwo':
        num = 2;
        break;
      case 'YearThree':
        num = 3;
        break;
      default:
        return '';
    }
    if (num <= 12) {
      return this.transloco.translate(`general.numberWords.${num}`);
    }
    return num.toString();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showNextButton(): boolean {
    return this.selectedIndex !== this.steps.length - 1;
  }

  onPreviousClick(): void {
    if (this.selectedIndex > 0) {
      this.showOtherOptions = false;
      this.selectedIndex -= 1;
    } else {
      if (this.data.isCustomTraining) {
        this.showOtherOptions = true;
        this.dataService.resetPhases();
        this.dataService.resetTopicArea();
        const originalCourse = this.dataService.getOriginallySelectedCourse();
        if (originalCourse) {
          toCourseGalleryItemDTO(
            originalCourse,
            this.courseGalleryService,
          ).subscribe({
            next: (galleryItem) => {
              this.dataService.setCourseId(0, galleryItem);
            },
          });
        }
        this.dataService.formGroupSettings.controls.learningPlanName.reset();
        this.data.isCustomTraining = false;
        this.isPreCuratedTraining = false;
      } else {
        this.dataService.reset();
        this.dialogRef.close();
        this.dialog.open(CourseDetailCardComponent, {
          autoFocus: false,
          ...DIALOG_SIZE_LARGE,
          data: {
            course: this.dataService.getOriginallySelectedCourse(),
          },
        });
      }
    }
  }

  onNextClick(): void {
    const { control } = this.steps[this.selectedIndex];
    if (control) {
      control.updateValueAndValidity();
      if (!control.valid) {
        control.markAllAsTouched();
        return;
      }
    }

    if (this.data.isCustomTraining && this.selectedIndex === 0) {
      const courseSelectionControl = this.dataService.formGroupCourseSelection;
      courseSelectionControl.updateValueAndValidity();
      if (!courseSelectionControl.valid) {
        courseSelectionControl.markAllAsTouched();
        return;
      }
    }

    if (this.selectedIndex < this.steps.length - 1) {
      this.trackSetupStep1();
      this.selectedIndex += 1;
    }
  }

  private trackSetupStep1(): void {
    const settings = this.dataService.formGroupSettings.controls;
    this.analyticsService.track('setup_step_1', {
      trainingName: settings.learningPlanName.value,
      startDate: this.toIsoDate(settings.startDate.value),
      duration: settings.duration.value,
    });
  }

  onSubmitClick(): void {
    this.trackSetupCompletion();
    const subscription = this.dataService.createTrainingWithEnrollment();
    if (subscription) {
      subscription.add(() => {
        this.dialogRef.close();
        this.dataService.reset();
      });
    } else {
      this.dialogRef.close();
      this.dataService.reset();
    }
  }

  private trackSetupCompletion(): void {
    const settings = this.dataService.formGroupSettings.controls;
    const trainingName = settings.learningPlanName.value;
    const startDate = this.toIsoDate(settings.startDate.value);
    const duration = settings.duration.value;

    this.analyticsService.track('setup_step_2', {
      participantsCount: this.dataService.selectedStudents.length,
      assignedLicenses: this.dataService.licensesToAssignCount,
    });

    if (this.isPreCuratedTraining) {
      this.analyticsService.track('setup_precurated_finish', {
        trainingName,
        startDate,
        duration,
      });
    } else if (this.data.isCustomTraining) {
      const courseNames = this.getSelectedCourseNames();
      this.analyticsService.track('setup_own_training_finish', {
        trainingName,
        startDate,
        duration,
        numberOfCourses: courseNames.length,
        courseNames,
      });
    }
  }

  private getSelectedCourseNames(): string[] {
    return this.dataService
      .getPhasesArray()
      .controls.map((phase) => phase.controls.course.value?.name)
      .filter((name): name is string => !!name);
  }

  private toIsoDate(date: Date | null): string | null {
    return date ? date.toISOString() : null;
  }

  onAddLicensesButtonClick(): void {
    this.dialogRef.afterClosed().subscribe(() => {
      void this.router.navigate(['/portal/billing']);
    });
    this.dialogRef.close();
  }

  onOpenOtherOptionsClick(): void {
    if (this.showOtherOptions || this.isAdvancedOptionsLocked) {
      return;
    }
    this.analyticsService.track('setup_add_more_courses');
    this.showOtherOptions = true;
  }

  toggleOtherOptions(event: Event): void {
    event.stopPropagation();
    if (this.isAdvancedOptionsLocked) {
      return;
    }
    this.showOtherOptions = !this.showOtherOptions;
  }

  onCustomTrainingClick() {
    this.analyticsService.track('setup_own_training_start', {
      courseName: this.dataService.getOriginallySelectedCourse()?.name ?? '',
    });
    this.data.isCustomTraining = true;
  }

  onPrecuratedClick() {
    this.analyticsService.track('setup_precurated_start', {
      courseName: this.dataService.getOriginallySelectedCourse()?.name ?? '',
      phase: this.currentPhase()?.name ?? '',
    });
    this.data.isCustomTraining = true;
    this.isPreCuratedTraining = true;
    const topicAreaId = this.precuratedLearningPlan()?.topicArea.id ?? null;
    const precuratedName = this.precuratedLearningPlan()?.name ?? null;
    const translationKey = `portal.trainings.precuratedLearningPlanCard.${precuratedName}.name`;
    const translatedName = this.transloco.translate(translationKey);
    this.dataService.formGroupSettings.controls.learningPlanName.setValue(
      translatedName,
    );
    this.courseGalleryService.getCourses(topicAreaId).subscribe({
      next: (courses) => {
        const phases = this.phases();
        const phasesArray = this.dataService.getPhasesArray();
        while (phasesArray.length < phases.length) {
          this.dataService.addPhase();
        }
        phases.forEach((phase, index) => {
          const galleryItem = courses.find((c) => c.id === phase.course.id);
          if (galleryItem) {
            this.dataService.setCourseId(index, galleryItem);
          }
        });
      },
    });
  }
}
